import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../utils/auth';
import { sensorAPI } from '../utils/api';
import { SensorData } from '../types';
import FuelTank from '../components/FuelTank';
import GeneratorDisplay from '../components/GeneratorDisplay';
import GenericSensor from '../components/GenericSensor';
import ViewTechBranding from '../components/ViewTechBranding';
import { formatSafeTimestamp } from '../utils/formatters';
import { 
  LogOut, 
  RefreshCw, 
  Activity, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Zap,
  MapPin,
  Filter,
  Monitor,
  FileText,
  Wrench
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [sensors, setSensors] = useState<SensorData[]>([]);
  const [filteredSensors, setFilteredSensors] = useState<SensorData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [availableDevices, setAvailableDevices] = useState<string[]>([]);

  const fetchSensors = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      console.log('Dashboard: Fetching sensors...', {
        isRefresh,
        token: localStorage.getItem('auth_token') ? 'present' : 'missing',
        user: user ? user.name : 'no user'
      });

      const response = await sensorAPI.getAllSensors();
      console.log('Dashboard: Sensors fetched successfully:', response);
      
      // Log sensor timestamps for debugging
      response.sensors.forEach((sensor: SensorData) => {
        if (sensor.current_value?.timestamp) {
          console.log(`Sensor ${sensor.id} timestamp:`, sensor.current_value.timestamp, 'Date:', new Date(sensor.current_value.timestamp));
        }
        if (sensor.device_info?.last_update) {
          console.log(`Sensor ${sensor.id} device last_update:`, sensor.device_info.last_update, 'Date:', new Date(sensor.device_info.last_update));
        }
      });
      
      setSensors(response.sensors);
      setLastUpdate(new Date());
      
      // Extract unique locations
      const locationMap: { [key: string]: boolean } = {};
      const locations: string[] = [];
      
      // Extract unique devices
      const deviceMap: { [key: string]: boolean } = {};
      const devices: string[] = [];
      
      response.sensors.forEach((sensor: SensorData) => {
        // Process locations
        const location = sensor.location;
        if (location && location.trim() !== '' && !locationMap[location]) {
          locationMap[location] = true;
          locations.push(location);
        }
        
        // Process devices
        const deviceName = sensor.device_info?.name || `Device ${sensor.gpswox_device_id}`;
        if (deviceName && !deviceMap[deviceName]) {
          deviceMap[deviceName] = true;
          devices.push(deviceName);
        }
      });
      
      locations.sort();
      devices.sort();
      setAvailableLocations(locations);
      setAvailableDevices(devices);
      
    } catch (err: any) {
      console.error('Dashboard: Error fetching sensors:', err);
      setError(err.response?.data?.message || 'Failed to fetch sensors');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Filter sensors based on location and device
  useEffect(() => {
    let filtered = sensors;
    
    // Apply location filter
    if (locationFilter !== 'all') {
      filtered = filtered.filter(sensor => sensor.location === locationFilter);
    }
    
    // Apply device filter
    if (deviceFilter !== 'all') {
      filtered = filtered.filter(sensor => {
        const deviceName = sensor.device_info?.name || `Device ${sensor.gpswox_device_id}`;
        return deviceName === deviceFilter;
      });
    }
    
    setFilteredSensors(filtered);
  }, [sensors, locationFilter, deviceFilter]);

  useEffect(() => {
    fetchSensors();
    
    // Auto-refresh every 30 seconds
    const interval: NodeJS.Timeout = setInterval(() => fetchSensors(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const renderSensorComponent = (sensor: SensorData) => {
    switch (sensor.type) {
      case 'fuel':
        return <FuelTank key={sensor.id} sensor={sensor} />;
      case 'engine_hours':
        return <GeneratorDisplay key={sensor.id} sensor={sensor} allSensors={sensors} />;
      default:
        return <GenericSensor key={sensor.id} sensor={sensor} />;
    }
  };

  const getOnlineSensorCount = () => {
    return filteredSensors.filter(sensor => 
      sensor.device_info?.status && 
      sensor.current_value?.value !== null && 
      sensor.current_value?.value !== undefined
    ).length;
  };

  const getOfflineSensorCount = () => {
    return filteredSensors.filter(sensor => 
      !sensor.device_info?.status || 
      sensor.current_value?.value === null || 
      sensor.current_value?.value === undefined
    ).length;
  };

  const getAlertsCount = () => {
    return filteredSensors.filter(sensor => {
      if (sensor.type === 'fuel' && sensor.current_value?.value !== null && sensor.current_value?.value !== undefined && sensor.current_value) {
        const value = sensor.current_value.value;
        const min = sensor.display_config?.min_value || 0;
        const max = sensor.display_config?.max_value || 100;
        const percentage = ((value - min) / (max - min)) * 100;
        return percentage < 20; // Low fuel alert
      }
      return false;
    }).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sensors...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <motion.header
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Zap className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">VT Diesel Panel</h1>
                <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/maintenance"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <Wrench className="w-4 h-4 mr-2" />
                Maintenance
              </Link>
              <Link
                to="/reports"
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <FileText className="w-4 h-4 mr-2" />
                Reports
              </Link>
              
              <button
                onClick={() => fetchSensors(true)}
                disabled={refreshing}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Stats Bar */}
      <motion.div
        className="bg-white border-b border-gray-200"
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
              <Activity className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-900">{filteredSensors.length}</p>
                <p className="text-sm text-blue-600">
                  {locationFilter === 'all' && deviceFilter === 'all' ? 'Total Sensors' :
                   locationFilter !== 'all' && deviceFilter === 'all' ? `Sensors in ${locationFilter}` :
                   locationFilter === 'all' && deviceFilter !== 'all' ? `Sensors on ${deviceFilter}` :
                   `Sensors in ${locationFilter} on ${deviceFilter}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-900">{getOnlineSensorCount()}</p>
                <p className="text-sm text-green-600">Online & Active</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-yellow-900">{getOfflineSensorCount()}</p>
                <p className="text-sm text-yellow-600">Offline/No Data</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-900">{getAlertsCount()}</p>
                <p className="text-sm text-red-600">Low Fuel Alerts</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <Clock className="w-8 h-8 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {formatSafeTimestamp(lastUpdate)}
                </p>
                <p className="text-sm text-gray-600">Last Update</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-lg font-medium text-gray-900">Sensors</h2>
            <div className="flex items-center space-x-4">
              {/* Location Filter */}
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="pl-8 pr-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Locations</option>
                  {availableLocations.map(location => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              
              {/* Device Filter */}
              <div className="relative">
                <Monitor className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                  className="pl-8 pr-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Devices</option>
                  {availableDevices.map(device => (
                    <option key={device} value={device}>{device}</option>
                  ))}
                </select>
              </div>
              
              {/* Clear Filters Button */}
              {(locationFilter !== 'all' || deviceFilter !== 'all') && (
                <button
                  onClick={() => {
                    setLocationFilter('all');
                    setDeviceFilter('all');
                  }}
                  className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {(locationFilter !== 'all' || deviceFilter !== 'all') && (
          <motion.div
            className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-2 text-blue-800">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Active filters:</span>
              {locationFilter !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                  <MapPin className="w-3 h-3 mr-1" />
                  Location: {locationFilter}
                </span>
              )}
              {deviceFilter !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700">
                  <Monitor className="w-3 h-3 mr-1" />
                  Device: {deviceFilter}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="text-red-800">{error}</span>
            </div>
          </motion.div>
        )}

        {sensors.length === 0 ? (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No Sensors Available</h3>
            <p className="text-gray-500">
              No sensors have been configured for your account yet. Please contact your administrator.
            </p>
          </motion.div>
        ) : (
          <motion.div
            className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {filteredSensors.map((sensor, index) => (
              <motion.div
                key={sensor.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {renderSensorComponent(sensor)}
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>

      {/* Footer with ViewTech Branding */}
      <footer className="bg-white border-t border-gray-200 py-4 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ViewTechBranding />
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;